import { FileSystemWorkspaceGateway } from "./file_system_workspace_gateway";
import type { WorkspaceGateway } from "./workspace_gateway";

export type WorkspacePickerIntent = "choose" | "create";

export interface ConnectedWorkspace {
  readonly handle: FileSystemDirectoryHandle;
  readonly gateway: WorkspaceGateway;
}

/** Separates direct picker gestures from remembered-handle reconnection. */
export interface WorkspaceConnector {
  isSupported(): boolean;
  choose(intent: WorkspacePickerIntent): Promise<ConnectedWorkspace>;
  fromHandle(handle: FileSystemDirectoryHandle): ConnectedWorkspace;
}

/** Connects user-granted native directories in supported Chromium browsers. */
export class BrowserWorkspaceConnector implements WorkspaceConnector {
  /** Detects the required picker without triggering it. */
  public isSupported(): boolean {
    return "showDirectoryPicker" in window;
  }

  /** Opens the native picker only from the caller's click or key gesture. */
  public async choose(intent: WorkspacePickerIntent): Promise<ConnectedWorkspace> {
    if (!this.isSupported()) {
      throw new Error("Shotboard requires a current Chromium browser with local folder access.");
    }

    const handle = await window.showDirectoryPicker({
      id: intent === "create" ? "shotboard-create-workspace" : "shotboard-workspace",
      mode: "readwrite",
      startIn: "documents",
    });
    return this.fromHandle(handle);
  }

  /** Rebuilds the gateway around a structured-cloned recent handle. */
  public fromHandle(handle: FileSystemDirectoryHandle): ConnectedWorkspace {
    return { handle, gateway: new FileSystemWorkspaceGateway(handle) };
  }
}
